import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { LOGO } from "@/components/Layout";
import { COMPANY_LINES } from "@/lib/company";

const CONTROLLER = "Trust Master S.r.l., Via Guglielmo Oberdan 23, 45100 Rovigo (RO), Italia — C.F./P.IVA 01683810293 — PEC trustmastersrl@pec.it";
const UPDATED = "4 ottobre 2026";

function Shell({ title, children, testId }) {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-[#0B132B] text-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center gap-3">
          <img src={LOGO} alt="Trust Master" className="h-9 w-9 rounded-md bg-white p-0.5 object-contain" />
          <span className="font-heading font-semibold">Trust Master CRM</span>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-10" data-testid={testId}>
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="legal-back-link"><ArrowLeft className="h-4 w-4" />Torna all'accesso</Link>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{title}</h1>
        <p className="text-xs text-slate-500 mt-2 mb-8">Ultimo aggiornamento: {UPDATED}</p>
        <div className="space-y-6 text-sm text-slate-700 leading-relaxed [&_h2]:font-heading [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-slate-900 [&_h2]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_table]:w-full [&_td]:border [&_td]:border-slate-200 [&_td]:p-2 [&_th]:border [&_th]:border-slate-200 [&_th]:p-2 [&_th]:bg-slate-50 [&_th]:text-left">
          {children}
        </div>
      </main>
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-6 text-[11px] leading-relaxed text-slate-500" data-testid="legal-company-footer">
          {COMPANY_LINES.map((l) => <p key={l}>{l}</p>)}
        </div>
      </footer>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <Shell title="Informativa privacy — Trust Master CRM" testId="privacy-page">
      <p>Informativa resa ai sensi degli artt. 13 e 14 del Regolamento (UE) 2016/679 (GDPR), del UK GDPR e del Data Protection Act 2018, agli utenti della piattaforma riservata Trust Master CRM (clienti, commercialisti, collaboratori e personale).</p>
      <section><h2>1. Titolare del trattamento</h2><p>{CONTROLLER}. Per le attività svolte nel Regno Unito: TrustMaster Ltd, 6-7 St Mary At Hill, London, England, EC3R 8EE. Contatto per la privacy: trustmastersrl@pec.it.</p></section>
      <section><h2>2. Dati trattati</h2>
        <ul>
          <li>Dati di identificazione dell'utenza: nome, email, profilo, Trust collegati.</li>
          <li>Dati di autenticazione e sicurezza: codici monouso (conservati solo come impronta crittografica), registrazione dell'app autenticatore (cifrata), data, ora, indirizzo IP e browser degli accessi, registro delle operazioni.</li>
          <li>Dati gestiti nella piattaforma per conto dei clienti: anagrafiche dei soggetti dei Trust, documenti di identità, dati patrimoniali, contabili e fiscali, documentazione antiriciclaggio.</li>
        </ul>
      </section>
      <section><h2>3. Finalità e basi giuridiche</h2>
        <ul>
          <li>Erogazione dei servizi di gestione dei Trust e del portale riservato: esecuzione del contratto (art. 6.1.b GDPR).</li>
          <li>Adempimenti di legge, inclusa la normativa antiriciclaggio (D.Lgs. 231/2007) e fiscale: obbligo legale (art. 6.1.c).</li>
          <li>Sicurezza della piattaforma, prevenzione di accessi non autorizzati, registri di audit secondo il sistema di gestione ISO/IEC 27001: legittimo interesse del titolare (art. 6.1.f) e obbligo di adottare misure adeguate (art. 32).</li>
        </ul>
      </section>
      <section><h2>4. Modalità e sicurezza</h2><p>I dati sono trattati con strumenti elettronici nell'ambito di un Sistema di Gestione della Sicurezza delle Informazioni conforme a ISO/IEC 27001:2022. Sono applicate le seguenti misure:</p>
        <ul>
          <li>accesso con codice monouso e, per i profili privilegiati, secondo fattore tramite app;</li>
          <li>segregazione dei dati per Trust;</li>
          <li>registro eventi protetto da modifica;</li>
          <li>backup cifrati AES-256;</li>
          <li>chiusura automatica della sessione dopo 30 minuti di inattività.</li>
        </ul>
      </section>
      <section><h2>5. Destinatari e trasferimenti</h2><p>I dati sono accessibili al personale autorizzato e ai soli utenti collegati al Trust interessato. Sono comunicati ai fornitori che operano come responsabili del trattamento ex art. 28 GDPR:</p>
        <ul>
          <li>infrastruttura cloud e hosting della piattaforma;</li>
          <li>servizio di invio email transazionali;</li>
          <li>servizio di protezione di rete Cloudflare.</li>
        </ul>
        <p>Eventuali trasferimenti fuori dallo Spazio Economico Europeo avvengono sulla base di decisioni di adeguatezza o di clausole contrattuali standard.</p></section>
      <section><h2>6. Conservazione</h2>
        <ul>
          <li>Dati dei Trust e documentazione antiriciclaggio: per la durata del rapporto e per 10 anni dalla sua cessazione.</li>
          <li>Log di accesso e registri di sicurezza: 12 mesi.</li>
          <li>Copie di backup: fino a 12 mesi, secondo la procedura TM-ISMS-07.</li>
          <li>Utenze inattive: sospese dopo 90 giorni.</li>
        </ul>
      </section>
      <section><h2>7. Diritti dell'interessato</h2><p>Puoi esercitare i diritti di accesso, rettifica, cancellazione, limitazione, portabilità e opposizione (artt. 15-22 GDPR) scrivendo a trustmastersrl@pec.it. Questi diritti valgono nei limiti degli obblighi di conservazione previsti dalla legge. Puoi inoltre proporre reclamo all'autorità di controllo: il Garante per la protezione dei dati personali (Italia) o l'Information Commissioner's Office (Regno Unito).</p></section>
      <section><h2>8. Cookie</h2><p>La piattaforma utilizza esclusivamente cookie tecnici necessari: vedi la <Link to="/cookie" className="text-blue-600 hover:underline">Cookie policy</Link>.</p></section>
    </Shell>
  );
}

export function CookiePage() {
  return (
    <Shell title="Cookie policy — Trust Master CRM" testId="cookie-page">
      <p>Trust Master CRM utilizza solo cookie e tecnologie strettamente necessari al funzionamento e alla sicurezza dell'area riservata. Non utilizza cookie di profilazione, di marketing o di analisi statistica, e non integra strumenti di tracciamento di terze parti. Per questo non è richiesto il consenso (art. 122 Codice Privacy, art. 5.3 Direttiva ePrivacy, PECR UK) e non è mostrato un banner.</p>
      <section><h2>Cookie utilizzati</h2>
        <div className="overflow-x-auto">
          <table data-testid="cookie-table">
            <thead><tr><th>Nome</th><th>Fornitore</th><th>Finalità</th><th>Durata</th><th>Tipo</th></tr></thead>
            <tbody>
              <tr><td className="font-mono">access_token</td><td>Trust Master (prima parte)</td><td>Mantiene la sessione autenticata. Cookie HttpOnly e Secure, non leggibile da script; le richieste che modificano dati sono protette da controllo di origine (anti-CSRF)</td><td>fino a 10 ore; la sessione si chiude dopo 30 minuti di inattività o al logout</td><td>Tecnico necessario</td></tr>
              <tr><td className="font-mono">__cf_bm</td><td>Cloudflare (terza parte)</td><td>Protezione da bot e attacchi automatizzati</td><td>30 minuti</td><td>Tecnico necessario (sicurezza)</td></tr>
            </tbody>
          </table>
        </div>
      </section>
      <section><h2>Altre tecnologie</h2><p>I caratteri tipografici e il logo sono serviti direttamente dalla piattaforma, senza richieste a servizi di terze parti come Google Fonts. Non vengono usati local storage o pixel a fini di tracciamento.</p></section>
      <section><h2>Gestione dei cookie</h2><p>Puoi cancellare i cookie dalle impostazioni del browser. Disabilitando il cookie di sessione non sarà possibile accedere all'area riservata.</p></section>
      <section><h2>Titolare e contatti</h2><p>{CONTROLLER}. Per maggiori informazioni consulta l'<Link to="/privacy" className="text-blue-600 hover:underline">Informativa privacy</Link>.</p></section>
    </Shell>
  );
}
